import type { ArtefactVersion } from "@esi/shared";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MermaidDiagram, SvgImage } from "@/components/diagram/Diagram";
import { Markdown } from "@/components/markdown/Markdown";
import { WideBlock } from "@/components/wide-block/WideBlock";
import { formatNumber } from "../../i18n/format.ts";
import { CodeView } from "./CodeView.tsx";
import { CSV_CELLS_SHOWN, CSV_ROWS_SHOWN, parseCsv } from "./csv.ts";
import { artefactPreviewHref, type ConversationScope } from "./queries.ts";

// The drawings moved to `components/diagram/` (Markdown draws Mermaid fences too, #380).
export { DiagramBlock, DiagramCanvas, naturalSize } from "@/components/diagram/Diagram";

/**
 * One artefact version, rendered by kind (design §4). Content is untrusted model output: no
 * renderer inserts it as HTML in the app's document.
 */
export function ArtefactBody({
  version,
  scope,
}: {
  version: ArtefactVersion;
  /** Where its conversation lives: a file artefact's preview route. */
  scope?: ConversationScope;
}) {
  const key = `${version.id}:${version.version}`;
  switch (version.kind) {
    case "markdown":
      // The panel's title is the h2: the document's own headings start at h3.
      return <Markdown markdown={version.content} headingBase={3} showLinkHost />;
    case "code":
      return <CodeView versionKey={key} code={version.content} language={version.language} />;
    case "csv":
      return <CsvTable title={version.title} content={version.content} />;
    case "svg":
      return <SvgImage svg={version.content} title={version.title} />;
    case "mermaid":
      return (
        <MermaidDiagram key={key} cacheKey={key} source={version.content} title={version.title} />
      );
    case "file":
      return <FileBody version={version} scope={scope} />;
  }
}

/**
 * A file a code run produced (agent-turn D19): a PNG or JPEG previewed as an image from the
 * API's preview route (never inline HTML or SVG); any other type is downloaded to open.
 */
function FileBody({ version, scope }: { version: ArtefactVersion; scope?: ConversationScope }) {
  const { t } = useTranslation();
  const type = version.file?.contentType ?? "";
  const image = type === "image/png" || type === "image/jpeg";
  if (image && scope && (version.file?.sizeBytes ?? 0) <= FILE_PREVIEW_MAX_BYTES) {
    return (
      <img
        src={artefactPreviewHref(scope, version.id, version.version)}
        alt={version.title}
        className="max-w-full rounded-lg border border-line bg-surface-raised"
      />
    );
  }
  return <p className="text-sm text-fg-secondary">{t("artefacts.file.downloadToOpen")}</p>;
}

/** The API previews images up to 4 MB (`FILE_ARTEFACT_PREVIEW_MAX_BYTES`). */
const FILE_PREVIEW_MAX_BYTES = 4 * 1024 * 1024;

/**
 * A CSV artefact as a table of text cells: the first row is the header (design §4). Short rows
 * are padded to the widest; at most {@link CSV_CELLS_SHOWN} cells are drawn, so a wide table shows
 * fewer rows (the notice says how many).
 */
export function CsvTable({ title, content }: { title: string; content: string }) {
  const { t } = useTranslation();
  const { rows, total } = useMemo(() => parseCsv(content, CSV_ROWS_SHOWN), [content]);
  const [header = [], ...parsed] = rows;
  const width = Math.max(header.length, ...parsed.map((r) => r.length));
  const shown = Math.min(parsed.length, Math.floor(CSV_CELLS_SHOWN / Math.max(width, 1)) - 1);
  const body = parsed.slice(0, Math.max(shown, 0));
  // Also under the table in Expand: the expanded view shows the same capped rows.
  const note =
    total === 0 ? (
      <p className="text-xs text-fg-muted">{t("artefacts.table.empty")}</p>
    ) : total > body.length ? (
      <p className="text-xs text-fg-muted">
        {t("artefacts.table.partial", {
          shown: formatNumber(body.length),
          total: formatNumber(total),
        })}
      </p>
    ) : null;
  return (
    <div className="flex flex-col gap-2">
      <WideBlock
        label={title}
        expand="always"
        note={note}
        className="rounded-lg border border-line bg-surface-raised"
      >
        <table className="w-full border-collapse text-left text-sm tabular-nums">
          <caption className="sr-only">{title}</caption>
          <thead className="sticky top-0 bg-surface-raised">
            <tr className="border-b border-line">
              {Array.from({ length: width }, (_, i) => (
                <th
                  // biome-ignore lint/suspicious/noArrayIndexKey: columns of an immutable version
                  key={i}
                  scope="col"
                  className="px-3 py-2 text-xs font-medium whitespace-nowrap text-fg-muted"
                >
                  {header[i] ?? ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {body.map((row, r) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: rows of an immutable version
              <tr key={r}>
                {Array.from({ length: width }, (_, c) => (
                  <td
                    // biome-ignore lint/suspicious/noArrayIndexKey: columns of an immutable version
                    key={c}
                    className="max-w-[24rem] px-3 py-1.5 align-top break-words whitespace-pre-wrap text-fg"
                  >
                    {row[c] ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </WideBlock>
      {note}
    </div>
  );
}
